import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Card, CardContent } from '@/components/ui/card'
import { formatDate } from '@/lib/cms/cms'
import { listPublishedPosts } from '@/lib/cms/server'
import { pageLinks, pageMeta } from '@/lib/seo'

export const Route = createFileRoute('/blog/')({
  loader: () => listPublishedPosts(),
  head: () => ({
    meta: pageMeta({
      title: 'Mobile Proxy Guides and Comparisons · Masklabs',
      description:
        'Guides, comparisons, and honest takes on metered proxies, mobile IPs, and web scraping, from the team building Masklabs. Pay for the bytes you actually use.',
      path: '/blog',
    }),
    links: pageLinks('/blog'),
  }),
  component: BlogIndex,
})

function BlogIndex() {
  const posts = Route.useLoaderData()
  return (
    <main className="w-full text-lg">
      <section className="border-b">
        <div className="mx-auto w-full max-w-3xl px-6 py-20">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Blog</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <h1 className="mt-6 text-4xl font-bold tracking-tight text-balance sm:text-5xl">
            Notes on metered proxies.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground text-pretty">
            Comparisons, guides, and the occasional opinion on proxies, mobile
            IPs, and paying for the bytes you actually use.
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-3xl px-6 py-16">
        <div className="flex flex-col gap-4">
          {posts.map((meta) => (
            <Link
              key={meta.slug}
              to="/blog/$slug"
              params={{ slug: meta.slug }}
              className="group block"
            >
              <Card className="transition-colors group-hover:border-primary/40">
                <CardContent className="flex flex-col gap-2 p-6">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{formatDate(meta.publishedAt)}</span>
                    {meta.readMinutes ? (
                      <>
                        <span aria-hidden>·</span>
                        <span>{meta.readMinutes} min read</span>
                      </>
                    ) : null}
                  </div>
                  <h2 className="text-xl font-semibold tracking-tight text-balance">
                    {meta.title}
                  </h2>
                  <p className="text-sm text-muted-foreground text-pretty">
                    {meta.description}
                  </p>
                  <span className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary">
                    Read
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>
    </main>
  )
}
